import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useExportFieldAdminHSPdfMutation, useGetFieldAdminHSDayQuery, useGetFieldAdminHSExportPreviewQuery } from "../fieldAdminApi";
import { getUserName } from "../../Authentication/authSlice";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { OK_GREEN_TEXT, teamLabel, parseLocalDate } from "../designTokens";
import { HSPageShell, HSSectionLabel } from "./HSStepShell";
import { FACheckbox, FAPrimaryButton } from "../FAControls";
import { FATextField } from "../StockFormShell";
import { PRESETS, thisMonthRange } from "./dateRangePresets";
import { downloadHSExport } from "./hsExport";

const INCLUDE_OPTIONS = [
  { key: "include_daily_logs", label: "Daily logs", sub: "Vehicle · tools · toolbox topic · attendance · sign-off", defaultOn: true },
  { key: "include_attendance_signatures", label: "Attendance signatures", sub: "Per-installer verification and conduct flags", defaultOn: true },
  { key: "include_fa_notes", label: "FA open notes", sub: "Free-form notes appended as a PDF appendix", defaultOn: false },
  { key: "include_photos", label: "Photo evidence", sub: "Vehicle inspection photos", defaultOn: false },
];

// "1 Aug – 19 Aug 2026" — the year is stated once, on the end of the range, as the design
// does. The raw ISO the date inputs hold is for the form, not for a document cover.
function coverRange(from, to) {
  if (!from || !to) return "Choose a date range";
  const opts = { day: "numeric", month: "short" };
  const start = parseLocalDate(from).toLocaleDateString(undefined, opts);
  const end = parseLocalDate(to).toLocaleDateString(undefined, { ...opts, year: "numeric" });
  return `${start} – ${end}`;
}

// .panel — the two columns of the export page.
function Panel({ title, right, children, sx }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "14px", p: "18px 18px 20px", ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mb: "14px" }}>
        <Typography sx={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.55)" }}>
          {title}
        </Typography>
        {right ? (
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.05em", color: "rgba(255,255,255,0.45)" }}>{right}</Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

// .pdf-meta-table .row — a label/value pair on the cover. Rendered only when there is a
// value: an empty VEHICLE row would tell the reader the file has one when it does not.
function CoverRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: "8px" }}>
      <Box component="span" sx={{ color: "#6B7079", fontWeight: 600, textTransform: "uppercase", fontSize: "0.58rem", letterSpacing: "0.06em", alignSelf: "center" }}>
        {label}
      </Box>
      <Box component="span" sx={{ fontWeight: 600 }}>{value}</Box>
    </Box>
  );
}

/** .pdf-stack / .pdf-page — the cover of the file that is about to be generated.
 *
 *  Everything here comes from the export preview endpoint, which reads the same team, range
 *  and bundle sequence the generator will use, so the panel cannot promise a cover the PDF
 *  does not produce. The design also shows a page count ("page 1 of 24"); that is not
 *  knowable before the document is rendered, so the caption states what it is instead.
 */
function CoverPreview({ preview, fromDate, toDate, includes, generatedLabel }) {
  const company = preview?.installer_company_name;
  const contents = [
    includes.include_daily_logs && "Daily logs",
    includes.include_attendance_signatures && "Toolbox-talk register",
    "Vehicle & tool inspections",
    includes.include_fa_notes && "FA notes",
    includes.include_photos && "Photo evidence",
    "Sign-off",
  ].filter(Boolean);

  return (
    <>
      <Box sx={{ position: "relative", p: "10px", backgroundColor: "rgba(255,255,255,0.02)", borderRadius: "10px" }}>
        <Box sx={{
          position: "relative", backgroundColor: "#F6F7F9", color: "#1A1F26", borderRadius: "4px",
          p: "26px 24px", boxShadow: "0 12px 30px rgba(0,0,0,0.45)", aspectRatio: "3 / 4",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pb: "16px", borderBottom: "2px solid #1A1F26", mb: "18px" }}>
            <Box sx={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: AMBER, color: "#1A1F26", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.04em", flexShrink: 0 }}>
              t
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
                {company ? `Tjommie · ${company}` : "Tjommie"}
              </Typography>
              <Typography sx={{ fontSize: "0.62rem", color: "#5C6068", mt: "1px" }}>
                {preview?.team_name ? teamLabel(preview.team_name, preview.team_id) : "—"}
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#5C6068", mb: "6px" }}>
            DoL-ready H&S file
          </Typography>
          <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, letterSpacing: "-0.02em", color: "#1A1F26", lineHeight: 1.15, mb: "8px" }}>
            Health &amp; Safety File
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#3D4148", fontWeight: 600, mb: "18px" }}>
            {coverRange(fromDate, toDate)}
            {preview ? ` · ${preview.record_count} daily record${preview.record_count === 1 ? "" : "s"}` : ""}
          </Typography>

          <Box sx={{ fontSize: "0.66rem", color: "#3D4148", lineHeight: 1.7, borderTop: "1px solid rgba(0,0,0,0.08)", pt: "10px" }}>
            <CoverRow label="FA" value={preview?.field_admin_name} />
            <CoverRow
              label="Team"
              value={preview?.team_name
                ? `${teamLabel(preview.team_name, preview.team_id)}${preview.installer_count ? ` (${preview.installer_count} installer${preview.installer_count === 1 ? "" : "s"})` : ""}`
                : null}
            />
            <CoverRow label="Vehicle" value={preview?.vehicle} />
            <CoverRow label="Zone" value={preview?.zone} />
            <CoverRow label="Generated" value={generatedLabel} />
            <CoverRow label="Bundle ID" value={preview?.next_bundle_id} />
          </Box>

          <Box sx={{ mt: "auto", fontSize: "0.6rem", color: "#6B7079", letterSpacing: "0.04em", textTransform: "uppercase", pt: "12px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <Box component="strong" sx={{ color: "#1A1F26" }}>Contents</Box>
            {` · ${contents.join(" · ")}`}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ textAlign: "center", fontSize: "0.74rem", color: "rgba(255,255,255,0.45)", mt: "12px" }}>
        Cover page · the bundle follows with one section per selected item
      </Typography>
    </>
  );
}

export default function Export() {
  const userName = useSelector(getUserName);
  const [exportPdf, { isLoading }] = useExportFieldAdminHSPdfMutation();
  const { data: day } = useGetFieldAdminHSDayQuery();

  const [thisMonthFrom, thisMonthTo] = thisMonthRange();
  const [fromDate, setFromDate] = useState(thisMonthFrom);
  const [toDate, setToDate] = useState(thisMonthTo);

  // Skipped until both bounds exist: the preview is scoped to the range, and asking for one
  // without it would 400 on the shared date-range validator.
  const { data: preview } = useGetFieldAdminHSExportPreviewQuery(
    { date_from: fromDate, date_to: toDate },
    { skip: !fromDate || !toDate },
  );
  // The cover states when the file was made. Nothing is generated yet, so this is "now" at
  // render — the same instant the bundle would carry if the button were pressed.
  const generatedLabel = new Date().toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const [includes, setIncludes] = useState(() => {
    const initial = {};
    INCLUDE_OPTIONS.forEach((opt) => { initial[opt.key] = opt.defaultOn; });
    return initial;
  });
  const [error, setError] = useState(null);

  const activePreset = PRESETS.find((p) => {
    const [from, to] = p.range();
    return from === fromDate && to === toDate;
  });

  const applyPreset = (preset) => {
    const [from, to] = preset.range();
    setFromDate(from);
    setToDate(to);
  };

  const toggleInclude = (key) => {
    setIncludes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) return;
    setError(null);  // clear any previous failure while this one is in flight
    const message = await downloadHSExport(exportPdf, { date_from: fromDate, date_to: toDate, ...includes });
    setError(message);
  };

  return (
    <HSPageShell>
      <Box sx={{ mb: "22px" }}>
        <Typography sx={{ fontSize: { xs: "1.35rem", sm: "1.6rem" }, fontWeight: 700, color: "#fff", letterSpacing: "-0.025em" }}>
          Export H&S PDF
        </Typography>
        <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "3px" }}>
          Bundle daily H&S records into a DoL-ready file.
        </Typography>
      </Box>

      {/* .layout — settings on the left, the cover of the file they describe on the right.
          Stacks below md: the cover is a reference, and on a phone the controls come first. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "18px", alignItems: "start", mb: "22px" }}>
      <Panel title="Bundle settings">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mb: "12px" }}>
          <FATextField label="From" type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FATextField label="To" type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>

        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", mb: "18px" }}>
          {PRESETS.map((preset) => (
            <Box
              key={preset.label}
              onClick={() => applyPreset(preset)}
              sx={{
                px: "12px",
                py: "6px",
                borderRadius: "999px",
                fontSize: "0.74rem",
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: activePreset?.label === preset.label ? "rgba(253,185,64,0.14)" : "rgba(255,255,255,0.04)",
                border: activePreset?.label === preset.label ? "1px solid rgba(253,185,64,0.35)" : "1px solid rgba(255,255,255,0.08)",
                color: activePreset?.label === preset.label ? AMBER : "rgba(255,255,255,0.7)",
              }}
            >
              {preset.label}
            </Box>
          ))}
          <Box
            sx={{
              px: "12px",
              py: "6px",
              borderRadius: "999px",
              fontSize: "0.74rem",
              fontWeight: 600,
              backgroundColor: !activePreset ? "rgba(253,185,64,0.14)" : "rgba(255,255,255,0.04)",
              border: !activePreset ? "1px solid rgba(253,185,64,0.35)" : "1px solid rgba(255,255,255,0.08)",
              color: !activePreset ? AMBER : "rgba(255,255,255,0.4)",
            }}
          >
            Custom
          </Box>
        </Box>

        {/* .field.locked — the design renders this as a read-only field rather than bare text,
            so it reads as part of the bundle's settings and not as a stray caption. It names
            the team as well as the person, because both go onto the cover page. */}
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.07em", mb: "6px" }}>Field Administrator</Typography>
        <Box sx={{
          backgroundColor: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "8px",
          padding: "10px 12px",
          fontSize: "0.88rem",
          color: "rgba(255,255,255,0.6)",
        }}>
          {[userName, day?.team_name ? teamLabel(day.team_name, day.team_id) : null].filter(Boolean).join(" · ") || "—"}
        </Box>

        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.07em", mt: "16px", mb: "8px" }}>
          Include in PDF
        </Typography>
        {/* Each option is its own bordered row and the whole surface toggles it — the
            label text was previously not a hit target, only the checkbox itself. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {INCLUDE_OPTIONS.map((opt) => (
          <Box
            key={opt.key}
            component="label"
            sx={{
              display: "flex", alignItems: "flex-start", gap: "10px", px: "12px", py: "10px", cursor: "pointer",
              backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "9px",
              "&:hover": { borderColor: "rgba(253,185,64,0.25)" },
            }}
          >
            <FACheckbox checked={includes[opt.key]} onChange={() => toggleInclude(opt.key)} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#fff", fontSize: "0.86rem", fontWeight: 600 }}>{opt.label}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem" }}>{opt.sub}</Typography>
            </Box>
          </Box>
        ))}
        </Box>
      </Panel>

      <Panel title="PDF cover preview" right="Cover page">
        <CoverPreview
          preview={preview}
          fromDate={fromDate}
          toDate={toDate}
          includes={includes}
          generatedLabel={generatedLabel}
        />
      </Panel>
      </Box>

      {/* Audit block — pure static copy, no backend dependency. */}
      <Box sx={{ backgroundColor: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.18)", borderRadius: "12px", px: "18px", py: "16px", mb: "22px" }}>
        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: "#fff", mb: "4px" }}>Audit · DoL-ready</Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)", mb: "10px" }}>
          This bundle replaces the per-team paper H&S file once Legal sign-off is confirmed.
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
          {[
            "Every daily checklist in the range, in date order",
            "Per-installer tool and attendance records",
            "Toolbox topics with acknowledgements",
            "Vehicle inspection results and captured photos",
          ].map((line) => (
            <Box component="li" key={line} sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)", display: "flex", gap: "8px" }}>
              <Box component="span" sx={{ color: OK_GREEN_TEXT }}>✓</Box>
              {line}
            </Box>
          ))}
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ color: "#F87171", fontSize: "0.82rem", mb: "12px" }}>{error}</Typography>
      ) : null}

      <FAPrimaryButton
        onClick={handleDownload}
        disabled={isLoading || !fromDate || !toDate}
        sx={{ width: "100%", py: "14px", borderRadius: "10px", fontSize: "0.9rem" }}
      >
        {isLoading ? "Generating…" : "Download PDF"}
      </FAPrimaryButton>
    </HSPageShell>
  );
}
